import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'

/**
 * Shared renderer for CONVERSATIONAL LLM text (chat replies, coach messages,
 * preview replies): sanitized GFM markdown with the app's element styling.
 *
 * Rendering rule (Workshop refresh #4, spec D3): conversational text renders
 * markdown through this component; PROMPT CONTENT (block text, composed
 * prompts, proposal before/after) deliberately renders plain
 * (`whitespace-pre-wrap`, code-ish) — prompt source must be shown literally.
 */
interface RenderedMessageProps {
  content: string
}

export function RenderedMessage({ content }: RenderedMessageProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={{
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className="min-w-full border-collapse border border-border text-sm">
              {children}
            </table>
          </div>
        ),
        thead: ({ children }) => (
          <thead className="bg-muted">{children}</thead>
        ),
        th: ({ children }) => (
          <th className="border border-border px-3 py-1.5 text-left font-medium">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-3 py-1.5">{children}</td>
        ),
        pre: ({ children }) => (
          <pre className="my-2 p-3 bg-muted rounded-lg overflow-x-auto text-sm font-mono">
            {children}
          </pre>
        ),
        code: ({ className, children }) => {
          // Fenced code blocks have className like "language-*" and are inside <pre>
          if (className) {
            return <code className={className}>{children}</code>
          }
          // Inline code
          return (
            <code className="px-1.5 py-0.5 bg-muted rounded-sm text-sm font-mono">
              {children}
            </code>
          )
        },
        ul: ({ children }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5">{children}</ol>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-[3px] border-border pl-3 my-2 italic text-muted-foreground">
            {children}
          </blockquote>
        ),
        h1: ({ children }) => <h1 className="text-xl font-bold mt-3 mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-bold mt-2.5 mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-semibold mt-2 mb-0.5">{children}</h3>,
        p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
        hr: () => <hr className="my-3 border-border" />,
      }}
    >
      {content}
    </ReactMarkdown>
  )
}
